'use client'

import { useFormikContext } from 'formik'
import { FormInput } from '@/components/form-inputs/form-input'
import { FormSelect } from '@/components/form-inputs/form-select'
import { FormSwitch } from '@/components/form-inputs/form-switch'
import { isDefaultFreePlanName } from '@/lib/billing/default-free-plan'
import type { BillingPlan } from '@/lib/billing/types'
import { FIELD_LABELS as L, NUMERIC_FIELDS as F, type SectionId } from './catalog'
import type { PlanEditorValues } from './model'
import { Benefits, SectionCard as Card, FieldGrid as Grid, Notice, NumberControl } from './fields'
import { ProviderSettings } from './provider-settings'

interface EditorSectionProps {
  section: SectionId
  planId?: string
  onSyncingChange: (syncing: boolean) => void
  onSynced: (plan: BillingPlan) => void
}

export function EditorSection({ section, planId, onSyncingChange, onSynced }: EditorSectionProps) {
  switch (section) {
    case 'overview': return <Overview creating={!planId} />
    case 'pricing': return <Pricing planId={planId} onSyncingChange={onSyncingChange} onSynced={onSynced} />
    case 'credits': return <Credits />
    case 'voice': return <Voice />
    case 'resources': return <Resources />
    case 'shopify': return <Shopify />
    case 'lifecycle': return <Lifecycle />
  }
}

function Overview({ creating }: { creating: boolean }) {
  const { values } = useFormikContext<PlanEditorValues>()
  const system = isDefaultFreePlanName(values.name)
  return <>
    <Card title="Plan identity">
      {system ? <Notice>System Free plans must have a zero price, their original payment channel, and Internal or Published visibility.</Notice> : null}
      <Grid>
        <FormInput name="display_name" label={L.display_name} placeholder="e.g. Growth" description="Shown to customers on pricing and billing pages." />
        <FormInput name="name" label={L.name} placeholder="e.g. growth-stripe" disabled={!creating} description="Unique identifier. Cannot be renamed after creation." />
      </Grid>
      <Grid>
        <FormSelect name="status" label={L.status} disabled={values.status === 'archived'} options={visibilityOptions(system, values.status)} description="Internal plans are assigned by an administrator." />
        <NumberControl field={F.order} />
      </Grid>
      {values.status === 'archived' ? <Notice>Restore this plan from the plan list before publishing it again.</Notice> : null}
    </Card>
    <Card title={L.benefits}><Benefits /></Card>
  </>
}

function Pricing({ planId, onSyncingChange, onSynced }: Omit<EditorSectionProps, 'section'>) {
  const { values } = useFormikContext<PlanEditorValues>()
  const fixedSystem = Boolean(planId) && isDefaultFreePlanName(values.name)
  return <>
    <Card title="Subscription price">
      <Grid>
        <NumberControl field={F.price} disabled={fixedSystem} />
        <FormSelect name="billing_channel" label={L.billing_channel} disabled={fixedSystem} options={[{ value: 'stripe', label: 'Stripe' }, { value: 'shopify', label: 'Shopify' }]} description="Determines the payment provider and which billing channel can use this plan." />
      </Grid>
    </Card>
    <Card
      title={values.billing_channel === 'shopify' ? 'Shopify billing' : 'Stripe billing'}
      className="border-violet-300 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30"
    >
      <ProviderSettings planId={planId} onSyncingChange={onSyncingChange} onSynced={onSynced} />
    </Card>
    <Card title="Text usage pricing"><Grid><NumberControl field={F.textMultiplier} /><NumberControl field={F.textMinimum} /><NumberControl field={F.textRounding} /></Grid></Card>
    <Card title="Voice usage pricing"><Grid><NumberControl field={F.voiceMultiplier} /><NumberControl field={F.voiceMinimum} /><NumberControl field={F.voiceRounding} /></Grid></Card>
    <Card title="Knowledge embedding pricing">
      <Grid><NumberControl field={F.embeddingMinimum} /><NumberControl field={F.embeddingRounding} /></Grid>
      <Notice>Uses the text cost multiplier. Rounding changes charges, not just their display.</Notice>
    </Card>
  </>
}

function Credits() {
  return <>
    <Card title="Credit grants">
      <NumberControl field={F.initial} />
      <Grid><NumberControl field={F.recurring} /><NumberControl field={F.payments} /></Grid>
      <FormSwitch name="plan_credits_expire" label={L.plan_credits_expire} description="Credits from each payment are valid until the end of the billing period they were paid for, plus 24 hours. Turn off to let them accumulate." />
      <Notice>Starting credits and payment credits are separate grants. Set an amount to 0 to turn off that grant.</Notice>
    </Card>
    <Card title="Purchased top-ups"><Grid><NumberControl field={F.topup} /><NumberControl field={F.bonus} /></Grid></Card>
    <Card title="Spending limits">
      <Grid><NumberControl field={F.daily} /><NumberControl field={F.monthly} /></Grid>
      <Notice>These caps limit usage. They do not set a maximum wallet balance or stop calls already in progress. Tenants with billing management access can set lower caps on their Billing page, never higher; the lower cap applies. Without a plan cap they can set any.</Notice>
    </Card>
  </>
}

function Voice() {
  const { values, errors, setValues } = useFormikContext<PlanEditorValues>()
  // Stays visible while a field needs fixing.
  const showTrial = values.voice_trial_enabled || Boolean(errors.voice_trial_duration_hours || errors.voice_trial_budget_microusd)
  function changeVoiceAccess(field: 'voice_included' | 'voice_trial_enabled', enabled: boolean) {
    const other = field === 'voice_included' ? 'voice_trial_enabled' : 'voice_included'
    void setValues(current => ({ ...current, [field]: enabled, [other]: enabled ? false : current[other] }))
  }
  return <>
    <Card title="Voice access">
      <FormSwitch name="voice_included" label={L.voice_included} description="Permanent voice access. Enabling this turns off Voice trial available; usage charges still apply." onValueChange={enabled => changeVoiceAccess('voice_included', enabled)} />
      <FormSwitch name="voice_trial_enabled" label={L.voice_trial_enabled} description="Trial access instead of permanent access. Enabling this turns off Voice included." onValueChange={enabled => changeVoiceAccess('voice_trial_enabled', enabled)} />
      {showTrial ? <>
        <Grid><NumberControl field={F.voiceDuration} /><NumberControl field={F.voiceBudget} /></Grid>
        <Notice>Duration and budget apply to trials started after saving; active trials keep their own.</Notice>
      </> : null}
      <Grid><NumberControl field={F.agents} /><NumberControl field={F.concurrency} /></Grid>
      {!values.voice_included && !values.voice_trial_enabled ? <Notice>This plan does not include voice or offer new trials. Existing active trials are checked separately; capacity settings remain saved.</Notice> : null}
    </Card>
    <Card title="Outbound campaigns">
      <FormSwitch name="campaigns_enabled" label={L.campaigns_enabled} description="Allow creating campaigns within the configured count limit." />
      <Notice>Starting campaign calls also requires included voice or an active voice trial, with sufficient balance and spending limits. Campaign access alone does not grant voice access.</Notice>
      <NumberControl field={F.campaigns} />
      <FormSwitch name="campaigns_trial_enabled" label={L.campaigns_trial_enabled} description="While a subscription is in its trial, turning this off blocks creating and starting campaigns and locks the Campaigns menu." />
    </Card>
    <Card title="Phone numbers & routing"><Grid><NumberControl field={F.numbers} /><NumberControl field={F.flows} /></Grid></Card>
  </>
}

function Resources() {
  return <>
    <Card title="Users & CRM"><Grid><NumberControl field={F.users} /><NumberControl field={F.crm} /></Grid></Card>
    <Card title="Knowledge bases">
      <NumberControl field={F.size} />
      <NumberControl field={F.knowledge} />
    </Card>
  </>
}

function Shopify() {
  return <>
    <Card title="Store connections"><NumberControl field={F.stores} /><Notice>Connecting Shopify stores is independent of the payment channel.</Notice></Card>
    <Card title="Analytics access">
      <NumberControl field={F.insights} />
      <Notice>Revenue and order totals, Orders and Catalog remain available after the Insights trial.</Notice>
    </Card>
    <Card title="Attribution channels"><NumberControl field={F.channels} /></Card>
  </>
}

function Lifecycle() {
  return <Card title="Workspace lifecycle"><NumberControl field={F.expiry} /><NumberControl field={F.grace} /></Card>
}

function visibilityOptions(system: boolean, status: string) {
  if (status === 'archived') return [{ value: 'archived', label: 'Archived' }]
  const options = [{ value: 'internal', label: 'Internal · admin assignment' }, { value: 'published', label: 'Published · available to customers' }]
  if (!system) options.unshift({ value: 'draft', label: 'Draft · not available yet' })
  return options
}
